import React, { useState, useEffect } from 'react';

const API_BASE = 'https://apispeccheck.zonetap.com';
const GOLD = '#D4AF37';

const AVAILABLE_CODES = [
  'ASHRAE 90.1', 'ASHRAE 62.1', 'CA Title 24', 'IBC', 'IMC',
  'IPC', 'IECC', 'NFPA 70 / NEC', 'SMACNA',
];

const SPEC_FORMATS = ['CSI MasterFormat', 'CSI UniFormat', 'Custom'];

const ALL_TABS = ['Company Info', 'Technical Standards', 'Manufacturers', 'Review Templates', 'Knowledge Base', 'Offices & Digest'];

const empty = () => ({
  companyName: '', address: '', city: '', state: '',
  licenseNumber: '', contactName: '', contactEmail: '', contactPhone: '',
  defaultCodes: [], seismicZone: '', climateZone: '',
  preferredSpecFormat: 'CSI MasterFormat',
  preferredManufacturers: [], excludedManufacturers: [],
  submittalStampLanguage: {
    approved: 'APPROVED',
    approvedAsNoted: 'APPROVED AS NOTED',
    reviseResubmit: 'REVISE AND RESUBMIT',
    rejected: 'REJECTED',
  },
  reportDisclaimer: 'This review is for general conformance with the design intent and contract documents only.',
  rfiResponseDisclaimer: '',
  signatureBlock: '',
});

const styles = {
  container: {
    maxWidth: 820,
    margin: '0 auto',
    padding: '32px 24px',
    fontFamily: 'sans-serif',
    color: '#1a1a1a',
  },
  heading: {
    fontSize: 22,
    fontWeight: 700,
    color: '#1a1a1a',
    marginBottom: 4,
  },
  subheading: {
    fontSize: 13,
    color: '#666',
    marginBottom: 24,
  },
  tabs: {
    display: 'flex',
    gap: 4,
    borderBottom: '2px solid #e5e7eb',
    marginBottom: 28,
  },
  tab: (active) => ({
    padding: '9px 18px',
    fontSize: 13,
    fontWeight: active ? 700 : 500,
    color: active ? GOLD : '#555',
    background: 'none',
    border: 'none',
    borderBottom: active ? `2px solid ${GOLD}` : '2px solid transparent',
    marginBottom: -2,
    cursor: 'pointer',
    fontFamily: 'sans-serif',
  }),
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: '24px 28px',
    marginBottom: 20,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: GOLD,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 16,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '14px 20px',
    marginBottom: 14,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
  },
  label: {
    fontSize: 12,
    fontWeight: 600,
    color: '#444',
  },
  input: {
    padding: '8px 10px',
    border: '1px solid #d1d5db',
    borderRadius: 5,
    fontSize: 13,
    fontFamily: 'sans-serif',
    color: '#1a1a1a',
    outline: 'none',
  },
  textarea: {
    padding: '8px 10px',
    border: '1px solid #d1d5db',
    borderRadius: 5,
    fontSize: 13,
    fontFamily: 'sans-serif',
    color: '#1a1a1a',
    outline: 'none',
    resize: 'vertical',
    minHeight: 72,
  },
  select: {
    padding: '8px 10px',
    border: '1px solid #d1d5db',
    borderRadius: 5,
    fontSize: 13,
    fontFamily: 'sans-serif',
    color: '#1a1a1a',
    background: '#fff',
  },
  checkGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '8px 12px',
  },
  checkLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    fontSize: 13,
    color: '#1a1a1a',
    cursor: 'pointer',
  },
  addRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 10,
  },
  addInput: {
    flex: 1,
    padding: '7px 10px',
    border: '1px solid #d1d5db',
    borderRadius: 5,
    fontSize: 13,
    fontFamily: 'sans-serif',
  },
  addBtn: {
    padding: '7px 14px',
    background: GOLD,
    color: '#fff',
    border: 'none',
    borderRadius: 5,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'sans-serif',
  },
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    background: '#f3f4f6',
    border: '1px solid #e5e7eb',
    borderRadius: 4,
    fontSize: 12,
    color: '#1a1a1a',
    margin: '3px 4px 3px 0',
  },
  removeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#9ca3af',
    fontSize: 14,
    lineHeight: 1,
    padding: 0,
  },
  saveBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    marginTop: 8,
  },
  saveBtn: {
    padding: '10px 28px',
    background: GOLD,
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'sans-serif',
  },
  savedMsg: {
    fontSize: 13,
    color: '#16a34a',
    fontWeight: 600,
  },
  errorMsg: {
    fontSize: 13,
    color: '#dc2626',
  },
};

function Field({ label, children }) {
  return (
    <div style={styles.field}>
      <label style={styles.label}>{label}</label>
      {children}
    </div>
  );
}

export default function FirmProfileSettings({ user }) {
  const [activeTab, setActiveTab] = useState(() => {
    const saved = Number(localStorage.getItem('caPlusFirmSettingsTab'));
    return Number.isInteger(saved) && saved >= 0 ? saved : 0;
  });
  const [form, setForm] = useState(empty());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  // Add-row state for manufacturer lists
  const [newPref, setNewPref] = useState({ name: '', category: '' });
  const [newExcl, setNewExcl] = useState({ name: '', reason: '' });
  const [newOffice, setNewOffice] = useState({ code: '', name: '', digestEmail: '' });
  const [digestSchedule, setDigestSchedule] = useState({ digestDayOfWeek: '', digestTime: '15:00' });
  const [savingDigest, setSavingDigest] = useState(false);
  const [digestSaved, setDigestSaved] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(null); // office code currently sending, or null
  const [digestSendResult, setDigestSendResult] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [engineerOverrideEmails, setEngineerOverrideEmails] = useState({});
  const [sendingEngineerDigest, setSendingEngineerDigest] = useState(null);
  const [engineerDigestResults, setEngineerDigestResults] = useState({});
  const [uploadingStampId, setUploadingStampId] = useState(null);
  const [uploadingFirmStamp, setUploadingFirmStamp] = useState(false);
  const [engineerSearch, setEngineerSearch] = useState('');
  const [showAllEngineers, setShowAllEngineers] = useState(false);

    const userId = user?.email || '';
  const isCaPlusAdmin = (form.caPlusAdminEmails || []).map((e) => e.toLowerCase()).includes(userId.toLowerCase());
  const TABS = isCaPlusAdmin ? ALL_TABS : ALL_TABS.filter((t) => t !== 'Offices & Digest');

  // Persist the active tab across refreshes; clamp if it's ever out of
  // range (e.g. a saved index for an admin-only tab, viewed by a
  // non-admin whose TABS array is shorter). Gated on `loading` being
  // false — isCaPlusAdmin depends on the profile fetch, so TABS.length
  // is briefly wrong on first render, before that fetch resolves.
  useEffect(() => {
    if (loading) return;
    if (activeTab >= TABS.length) {
      setActiveTab(0);
      return;
    }
    localStorage.setItem('caPlusFirmSettingsTab', String(activeTab));
  }, [activeTab, TABS.length, loading]);

  useEffect(() => {
    if (!userId) return;
    fetch(`${API_BASE}/api/firm-profile?userId=${encodeURIComponent(userId)}`)
      .then(r => r.json())
      .then(data => {
        if (data.profile) {
          setForm(f => ({ ...f, ...data.profile }));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    fetch(`${API_BASE}/api/ca-plus/employees?userId=${encodeURIComponent(userId)}`)
      .then(r => r.json())
      .then(data => setEmployees(data.employees || []))
      .catch(() => {});
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    fetch(`${API_BASE}/api/features?userId=${encodeURIComponent(userId)}`)
      .then(r => r.json())
      .then(data => {
        setDigestSchedule({
          digestDayOfWeek: data.digestDayOfWeek ?? '',
          digestTime: data.digestTime || '15:00',
        });
      })
      .catch(() => {});
  }, [userId]);

  // Knowledge Base tab — uploads/deletes happen immediately, separate from
  // the Company Info form's staged/save pattern above.
  const [knowledgeDocs, setKnowledgeDocs] = useState([]);
  const [knowledgeLoading, setKnowledgeLoading] = useState(false);
  const [knowledgeUploading, setKnowledgeUploading] = useState(false);
  const [knowledgeError, setKnowledgeError] = useState('');

  const loadKnowledgeDocs = () => {
    if (!userId) return;
    setKnowledgeLoading(true);
    fetch(`${API_BASE}/api/company-knowledge?userId=${encodeURIComponent(userId)}`)
      .then(r => r.json())
      .then(data => setKnowledgeDocs(data.docs || []))
      .catch(() => {})
      .finally(() => setKnowledgeLoading(false));
  };

  useEffect(() => {
    if (activeTab === 4) loadKnowledgeDocs();
  }, [activeTab, userId]);

  const uploadKnowledgeDoc = async (file) => {
    if (!file || !userId) return;
    setKnowledgeUploading(true);
    setKnowledgeError('');
    try {
      const formData = new FormData();
      formData.append('document', file);
      const res = await fetch(`${API_BASE}/api/company-knowledge?userId=${encodeURIComponent(userId)}`, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      loadKnowledgeDocs();
    } catch (e) {
      setKnowledgeError(e.message);
    } finally {
      setKnowledgeUploading(false);
    }
  };

  const deleteKnowledgeDoc = async (id) => {
    if (!window.confirm('Delete this document? This cannot be undone.')) return;
    try {
      const res = await fetch(`${API_BASE}/api/company-knowledge/${id}?userId=${encodeURIComponent(userId)}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Delete failed');
      setKnowledgeDocs(docs => docs.filter(d => d._id !== id));
    } catch (e) {
      setKnowledgeError(e.message);
    }
  };

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const setStamp = (key, value) =>
    setForm(f => ({ ...f, submittalStampLanguage: { ...f.submittalStampLanguage, [key]: value } }));

  const toggleCode = (code) =>
    setForm(f => ({
      ...f,
      defaultCodes: f.defaultCodes.includes(code)
        ? f.defaultCodes.filter(c => c !== code)
        : [...f.defaultCodes, code],
    }));

  const addPref = () => {
    if (!newPref.name.trim()) return;
    setForm(f => ({ ...f, preferredManufacturers: [...f.preferredManufacturers, { ...newPref }] }));
    setNewPref({ name: '', category: '' });
  };

  const removePref = (i) =>
    setForm(f => ({ ...f, preferredManufacturers: f.preferredManufacturers.filter((_, j) => j !== i) }));

  const addExcl = () => {
    if (!newExcl.name.trim()) return;
    setForm(f => ({ ...f, excludedManufacturers: [...f.excludedManufacturers, { ...newExcl }] }));
    setNewExcl({ name: '', reason: '' });
  };

  const removeExcl = (i) =>
    setForm(f => ({ ...f, excludedManufacturers: f.excludedManufacturers.filter((_, j) => j !== i) }));

  const saveOfficesNow = async (updatedOffices) => {
    try {
      await fetch(`${API_BASE}/api/firm-profile?userId=${encodeURIComponent(userId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, offices: updatedOffices }),
      });
    } catch (err) {
      alert('Failed to save: ' + err.message);
    }
  };

  const addOffice = () => {
    if (!newOffice.code.trim() || !newOffice.name.trim()) return;
    const updated = [...(form.offices || []), { ...newOffice }];
    setForm(f => ({ ...f, offices: updated }));
    setNewOffice({ code: '', name: '', digestEmail: '' });
    saveOfficesNow(updated);
  };

  const removeOffice = (i) => {
    const updated = form.offices.filter((_, j) => j !== i);
    setForm(f => ({ ...f, offices: updated }));
    saveOfficesNow(updated);
  };

  const updateOfficeDigestEmail = (i, value) =>
    setForm(f => ({ ...f, offices: f.offices.map((o, j) => j === i ? { ...o, digestEmail: value } : o) }));

  const saveOfficeDigestEmail = (i) => saveOfficesNow(form.offices);

    const uploadStampFor = async (stampId, file) => {
      setUploadingStampId(stampId);
      try {
        const formData = new FormData();
        formData.append('userId', userId);
        formData.append('stamp', file);
        const res = await fetch(`${API_BASE}/api/ca-plus/stamp/${stampId}/upload`, {
          method: 'POST',
          body: formData,
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to upload stamp');
        // Re-fetch rather than optimistically patch local state — the
        // upload response only returns a filename, not the full
        // stampImageRef (s3Key, mimetype). Patching with a partial object
        // risks a later "Save Profile" click overwriting the database's
        // correct, complete record with an incomplete one.
        const profileRes = await fetch(`${API_BASE}/api/firm-profile?userId=${encodeURIComponent(userId)}`);
        const profileData = await profileRes.json();
        if (profileData.profile) setForm((f) => ({ ...f, caPlusStampOptions: profileData.profile.caPlusStampOptions }));
      } catch (err) {
        alert(err.message);
      } finally {
        setUploadingStampId(null);
      }
    };

    const deleteStampFor = async (stampId) => {
      if (!window.confirm('Remove this stamp graphic? It will fall back to the generic text-based stamp until a new one is uploaded.')) return;
      try {
        const res = await fetch(`${API_BASE}/api/ca-plus/stamp/${stampId}?userId=${encodeURIComponent(userId)}`, {
          method: 'DELETE',
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to remove stamp');
        set('caPlusStampOptions', (form.caPlusStampOptions || []).map((s) =>
          s.id === stampId ? { ...s, stampImageRef: null } : s
        ));
      } catch (err) {
        alert(err.message);
      }
    };

    const sendEngineerDigestNow = async (emp) => {
    setSendingEngineerDigest(emp.email);
    try {
      const res = await fetch(`${API_BASE}/api/ca-plus/digest/send-engineer-now`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          reviewerEmail: emp.email,
          reviewerName: emp.name,
          overrideEmail: engineerOverrideEmails[emp.email] || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send digest');
      setEngineerDigestResults((r) => ({ ...r, [emp.email]: data.result }));
    } catch (err) {
      alert(err.message);
    } finally {
      setSendingEngineerDigest(null);
    }
  };

  const saveDigestSchedule = async () => {
    setSavingDigest(true);
    setDigestSaved(false);
    try {
      await fetch(`${API_BASE}/api/ca-plus/digest/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          digestDayOfWeek: digestSchedule.digestDayOfWeek,
          digestTime: digestSchedule.digestTime,
        }),
      });
      setDigestSaved(true);
      setTimeout(() => setDigestSaved(false), 3000);
    } catch (err) {
      alert(err.message);
    } finally {
      setSavingDigest(false);
    }
  };

  const sendDigestNow = async (officeCode) => {
    setSendingDigest(officeCode);
    setDigestSendResult(null);
    try {
      const res = await fetch(`${API_BASE}/api/ca-plus/digest/send-now`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, officeCode }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send digest');
      setDigestSendResult(data.results?.[0]);
    } catch (err) {
      alert(err.message);
    } finally {
      setSendingDigest(null);
    }
  };

  const save = async () => {
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      const payload = {
        ...form,
        // Guard against an emptied-out number field — fall back to the
        // default rather than saving an invalid/blank value.
        caPlusDueDateWarningDays: form.caPlusDueDateWarningDays === '' || form.caPlusDueDateWarningDays == null
          ? 3
          : form.caPlusDueDateWarningDays,
      };
      const res = await fetch(`${API_BASE}/api/firm-profile?userId=${encodeURIComponent(userId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Save failed');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 40, color: '#666', fontFamily: 'sans-serif' }}>Loading profile…</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.heading}>Company Intelligence Profile</div>
      <div style={styles.subheading}>
        These settings personalize every report, letter, and analysis AECWorks AI Tools generates for your firm.
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        {TABS.map((t, i) => (
          <button key={t} style={styles.tab(activeTab === i)} onClick={() => setActiveTab(i)}>
            {t}
          </button>
        ))}
      </div>

      {/* Tab 1: Company Info */}
      {activeTab === 0 && (
        <div style={styles.card}>
          <div style={styles.sectionTitle}>Company Info</div>
          <div style={styles.row}>
            <Field label="Company Name">
              <input style={styles.input} value={form.companyName} onChange={e => set('companyName', e.target.value)} />
            </Field>
            <Field label="License Number">
              <input style={styles.input} value={form.licenseNumber} onChange={e => set('licenseNumber', e.target.value)} />
            </Field>
          </div>
          <div style={{ ...styles.row, gridTemplateColumns: '2fr 1fr 1fr' }}>
            <Field label="Address">
              <input style={styles.input} value={form.address} onChange={e => set('address', e.target.value)} />
            </Field>
            <Field label="City">
              <input style={styles.input} value={form.city} onChange={e => set('city', e.target.value)} />
            </Field>
            <Field label="State">
              <input style={styles.input} value={form.state} onChange={e => set('state', e.target.value)} />
            </Field>
          </div>
          <div style={styles.row}>
            <Field label="Primary Contact Name">
              <input style={styles.input} value={form.contactName} onChange={e => set('contactName', e.target.value)} />
            </Field>
            <Field label="Contact Email">
              <input style={styles.input} type="email" value={form.contactEmail} onChange={e => set('contactEmail', e.target.value)} />
            </Field>
          </div>
          <div style={{ ...styles.row, gridTemplateColumns: '1fr 2fr' }}>
            <Field label="Contact Phone">
              <input style={styles.input} value={form.contactPhone} onChange={e => set('contactPhone', e.target.value)} />
            </Field>
          </div>
        </div>
      )}

      {/* Tab 2: Technical Standards */}
      {activeTab === 1 && (
        <div style={styles.card}>
          <div style={styles.sectionTitle}>Technical Standards</div>
          <Field label="Default Codes & Standards">
            <div style={{ ...styles.checkGrid, marginTop: 6 }}>
              {AVAILABLE_CODES.map(code => (
                <label key={code} style={styles.checkLabel}>
                  <input
                    type="checkbox"
                    checked={form.defaultCodes.includes(code)}
                    onChange={() => toggleCode(code)}
                    style={{ accentColor: GOLD }}
                  />
                  {code}
                </label>
              ))}
            </div>
          </Field>
          <div style={{ ...styles.row, marginTop: 18 }}>
            <Field label="Seismic Zone">
              <input style={styles.input} placeholder="e.g. SDC D" value={form.seismicZone} onChange={e => set('seismicZone', e.target.value)} />
            </Field>
            <Field label="Climate Zone">
              <input style={styles.input} placeholder="e.g. Zone 6" value={form.climateZone} onChange={e => set('climateZone', e.target.value)} />
            </Field>
          </div>
          <div style={{ ...styles.row, gridTemplateColumns: '1fr 2fr', marginTop: 0 }}>
            <Field label="Preferred Spec Format">
              <select style={styles.select} value={form.preferredSpecFormat} onChange={e => set('preferredSpecFormat', e.target.value)}>
                {SPEC_FORMATS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </Field>
          </div>
        </div>
      )}

      {/* Tab 3: Manufacturers */}
      {activeTab === 2 && (
        <>
          <div style={styles.card}>
            <div style={styles.sectionTitle}>Preferred Manufacturers</div>
            <div style={styles.addRow}>
              <input
                style={styles.addInput}
                placeholder="Manufacturer name"
                value={newPref.name}
                onChange={e => setNewPref(p => ({ ...p, name: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && addPref()}
              />
              <input
                style={styles.addInput}
                placeholder="Category (e.g. HVAC)"
                value={newPref.category}
                onChange={e => setNewPref(p => ({ ...p, category: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && addPref()}
              />
              <button style={styles.addBtn} onClick={addPref}>+ Add</button>
            </div>
            <div>
              {form.preferredManufacturers.length === 0 && (
                <div style={{ fontSize: 13, color: '#9ca3af' }}>No preferred manufacturers added yet.</div>
              )}
              {form.preferredManufacturers.map((m, i) => (
                <span key={i} style={styles.tag}>
                  <strong>{m.name}</strong>{m.category ? ` — ${m.category}` : ''}
                  <button style={styles.removeBtn} onClick={() => removePref(i)}>✕</button>
                </span>
              ))}
            </div>
          </div>

          <div style={styles.card}>
            <div style={styles.sectionTitle}>Excluded Manufacturers</div>
            <div style={styles.addRow}>
              <input
                style={styles.addInput}
                placeholder="Manufacturer name"
                value={newExcl.name}
                onChange={e => setNewExcl(p => ({ ...p, name: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && addExcl()}
              />
              <input
                style={styles.addInput}
                placeholder="Reason (e.g. Not approved)"
                value={newExcl.reason}
                onChange={e => setNewExcl(p => ({ ...p, reason: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && addExcl()}
              />
              <button style={styles.addBtn} onClick={addExcl}>+ Add</button>
            </div>
            <div>
              {form.excludedManufacturers.length === 0 && (
                <div style={{ fontSize: 13, color: '#9ca3af' }}>No excluded manufacturers added yet.</div>
              )}
              {form.excludedManufacturers.map((m, i) => (
                <span key={i} style={styles.tag}>
                  <strong>{m.name}</strong>{m.reason ? ` — ${m.reason}` : ''}
                  <button style={styles.removeBtn} onClick={() => removeExcl(i)}>✕</button>
                </span>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Tab 4: Review Templates */}
      {activeTab === 3 && (
        <div style={styles.card}>
          <div style={styles.sectionTitle}>Submittal Stamp Language</div>
          <div style={styles.row}>
            <Field label="Approved">
              <input style={styles.input} value={form.submittalStampLanguage.approved} onChange={e => setStamp('approved', e.target.value)} />
            </Field>
            <Field label="Approved as Noted">
              <input style={styles.input} value={form.submittalStampLanguage.approvedAsNoted} onChange={e => setStamp('approvedAsNoted', e.target.value)} />
            </Field>
            <Field label="Revise & Resubmit">
              <input style={styles.input} value={form.submittalStampLanguage.reviseResubmit} onChange={e => setStamp('reviseResubmit', e.target.value)} />
            </Field>
            <Field label="Rejected">
              <input style={styles.input} value={form.submittalStampLanguage.rejected} onChange={e => setStamp('rejected', e.target.value)} />
            </Field>
          </div>

          <div style={styles.sectionTitle}>Comments Location</div>
          <Field label="Where AI comments appear on the returned submittal">
            <select
              style={styles.input}
              value={form.caPlusCommentsLocation || 'separate'}
              onChange={e => set('caPlusCommentsLocation', e.target.value)}
            >
              <option value="separate">Separate report (today's default — two downloads)</option>
              <option value="attachment">Attached — inserted right after the cover page of the stamped document</option>
            </select>
          </Field>

          <div style={styles.sectionTitle}>Disclaimers & Signature</div>
          <Field label="Report Disclaimer">
            <textarea
              style={styles.textarea}
              value={form.reportDisclaimer}
              onChange={e => set('reportDisclaimer', e.target.value)}
            />
          </Field>
          <div style={{ marginTop: 14 }}>
            <Field label="RFI Response Disclaimer">
              <textarea
                style={styles.textarea}
                value={form.rfiResponseDisclaimer}
                onChange={e => set('rfiResponseDisclaimer', e.target.value)}
              />
            </Field>
          </div>
          <div style={{ marginTop: 14 }}>
            <Field label="Signature Block">
              <textarea
                style={styles.textarea}
                placeholder={'e.g.\nJohn Smith, PE\nMechanical Engineer of Record\nLicense #M12345'}
                value={form.signatureBlock}
                onChange={e => set('signatureBlock', e.target.value)}
              />
            </Field>
          </div>
        </div>
      )}

      {activeTab === 4 && (
        <div style={styles.card}>
          <div style={styles.sectionTitle}>Company Reference Documents</div>
          <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
            Upload mission statements, project summaries, or standard practices — the AI Manager's
            General Consultation mode will reference these alongside your firm's other settings.
          </p>

          <label style={{
            display: 'inline-block',
            padding: '9px 18px',
            fontSize: 13,
            fontWeight: 600,
            color: '#1a1a1a',
            background: '#f3f4f6',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            cursor: knowledgeUploading ? 'not-allowed' : 'pointer',
            opacity: knowledgeUploading ? 0.6 : 1,
          }}>
            {knowledgeUploading ? 'Uploading…' : '+ Upload Document'}
            <input
              type="file"
              accept=".pdf,.doc,.docx,.txt"
              style={{ display: 'none' }}
              disabled={knowledgeUploading}
              onChange={(e) => {
                const file = e.target.files[0];
                e.target.value = '';
                if (file) uploadKnowledgeDoc(file);
              }}
            />
          </label>

          {knowledgeError && <div style={{ color: '#dc2626', fontSize: 13, marginTop: 10 }}>{knowledgeError}</div>}

          <div style={{ marginTop: 20 }}>
            {knowledgeLoading && <p style={{ fontSize: 13, color: '#666' }}>Loading…</p>}
            {!knowledgeLoading && knowledgeDocs.length === 0 && (
              <p style={{ fontSize: 13, color: '#9ca3af' }}>No documents uploaded yet.</p>
            )}
            {knowledgeDocs.map((doc) => (
              <div key={doc._id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: 6, marginBottom: 8,
              }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{doc.originalName}</div>
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>
                    {doc.fileSize ? `${(doc.fileSize / 1024).toFixed(0)} KB` : ''}
                    {doc.createdAt ? ` · ${new Date(doc.createdAt).toLocaleDateString()}` : ''}
                  </div>
                </div>
                <button
                  onClick={() => deleteKnowledgeDoc(doc._id)}
                  style={{
                    fontSize: 12, color: '#dc2626', background: 'none',
                    border: '1px solid #fca5a5', borderRadius: 5, padding: '5px 10px', cursor: 'pointer',
                  }}
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 5 && (
        <>
          <div style={styles.card}>
            <div style={styles.sectionTitle}>Due Date Warnings</div>
            <Field label="Show a warning color when due within this many days">
              <input
                type="number"
                min="0"
                style={styles.input}
                value={form.caPlusDueDateWarningDays ?? 3}
                onChange={e => set('caPlusDueDateWarningDays', e.target.value === '' ? '' : Number(e.target.value))}
              />
            </Field>
            <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 8, marginBottom: 0 }}>
              Items past due always show as critical, regardless of this setting.
            </p>
          </div>

          <div style={styles.card}>
            <div style={styles.sectionTitle}>Offices</div>
            <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
              Each office can have its own recipient for the weekly Open Items digest — a distribution
              list works well here, so it doesn't depend on any one person.
            </p>
            {(form.offices || []).length === 0 && (
              <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 12 }}>No offices added yet.</div>
            )}
            {(form.offices || []).map((o, i) => (
              <div key={i} style={{ ...styles.addRow, alignItems: 'center', marginBottom: 8 }}>
                <div style={{ minWidth: 60, fontWeight: 600, fontSize: 13 }}>{o.code}</div>
                <div style={{ minWidth: 160, fontSize: 13 }}>{o.name}</div>
                <input
                  style={styles.addInput}
                  placeholder="Digest recipient email (e.g. a distribution list)"
                  value={o.digestEmail || ''}
                  onChange={e => updateOfficeDigestEmail(i, e.target.value)}
                  onBlur={() => saveOfficeDigestEmail(i)}
                />
                <button
                  style={styles.secondaryBtn || styles.addBtn}
                  disabled={sendingDigest === o.code || !o.digestEmail}
                  onClick={() => sendDigestNow(o.code)}
                >
                  {sendingDigest === o.code ? 'Sending...' : 'Send Now'}
                </button>
                <a
                
                  href={`${API_BASE}/api/ca-plus/digest/export?userId=${encodeURIComponent(userId)}&officeCode=${encodeURIComponent(o.code)}`}
                  title="Download as Excel"
                  style={{
                    ...styles.secondaryBtn, textDecoration: 'none', display: 'inline-flex',
                    alignItems: 'center', justifyContent: 'center', padding: '9px 12px', fontSize: 16,
                  }}
                >
                  ⬇
                </a>
                <button style={styles.removeBtn} onClick={() => removeOffice(i)}>✕</button>
              </div>
            ))}
            {digestSendResult && (
              <div style={{ fontSize: 13, color: digestSendResult.sent ? '#166534' : '#dc2626', marginTop: 8 }}>
                {digestSendResult.sent
                  ? `✓ Sent — ${digestSendResult.count} open item(s) included.`
                  : `Failed: ${digestSendResult.reason || 'unknown error'}`}
              </div>
            )}

            <div style={{ ...styles.addRow, marginTop: 16 }}>
              <input
                style={styles.addInput}
                placeholder="Code (e.g. SF)"
                value={newOffice.code}
                onChange={e => setNewOffice(p => ({ ...p, code: e.target.value }))}
              />
              <input
                style={styles.addInput}
                placeholder="Office name (e.g. San Francisco)"
                value={newOffice.name}
                onChange={e => setNewOffice(p => ({ ...p, name: e.target.value }))}
              />
              <input
                style={styles.addInput}
                placeholder="Digest recipient email (optional)"
                value={newOffice.digestEmail}
                onChange={e => setNewOffice(p => ({ ...p, digestEmail: e.target.value }))}
              />
              <button style={styles.addBtn} onClick={addOffice}>+ Add Office</button>
            </div>
          </div>

          <div style={styles.card}>
            <div style={styles.sectionTitle}>Digest Schedule</div>
            <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
              Choose when the weekly digest sends automatically — leave the day blank to disable
              automatic sending (you can still use "Send Now" above anytime).
            </p>
            <div style={styles.row}>
              <Field label="Day of Week">
                <select
                  style={styles.input}
                  value={digestSchedule.digestDayOfWeek}
                  onChange={e => setDigestSchedule(p => ({ ...p, digestDayOfWeek: e.target.value }))}
                >
                  <option value="">Never (disabled)</option>
                  <option value="0">Sunday</option>
                  <option value="1">Monday</option>
                  <option value="2">Tuesday</option>
                  <option value="3">Wednesday</option>
                  <option value="4">Thursday</option>
                  <option value="5">Friday</option>
                  <option value="6">Saturday</option>
                </select>
              </Field>
              <Field label="Time of Day (server time)">
                <input
                  style={styles.input}
                  type="time"
                  value={digestSchedule.digestTime}
                  onChange={e => setDigestSchedule(p => ({ ...p, digestTime: e.target.value }))}
                />
              </Field>
            </div>
            <button style={{ ...styles.saveBtn, opacity: savingDigest ? 0.7 : 1 }} onClick={saveDigestSchedule} disabled={savingDigest}>
              {savingDigest ? 'Saving…' : 'Save Schedule'}
            </button>
            {digestSaved && <span style={{ ...styles.savedMsg, marginLeft: 12 }}>✓ Schedule saved</span>}
          </div>

          <div style={styles.card}>
            <div style={styles.sectionTitle}>Engineer Digests</div>
            <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
              Send an individual their own open-items digest — just their assigned Submittals and RFIs.
              Sends to their own email by default; enter an address below to redirect it instead.
            </p>
            {employees.length > 0 && (
              <input
                style={{ ...styles.addInput, width: '100%', marginBottom: 12 }}
                placeholder="Search by name or email..."
                value={engineerSearch}
                onChange={(e) => setEngineerSearch(e.target.value)}
              />
            )}
            {employees.length === 0 && (
              <div style={{ fontSize: 13, color: '#9ca3af' }}>No employees found yet.</div>
            )}
            {(() => {
              const filtered = employees.filter((emp) =>
                (emp.name || '').toLowerCase().includes(engineerSearch.toLowerCase()) ||
                (emp.email || '').toLowerCase().includes(engineerSearch.toLowerCase())
              );
              const visible = showAllEngineers || engineerSearch ? filtered : filtered.slice(0, 5);
              return visible;
            })().map((emp) => (
              <div key={emp.email} style={{ ...styles.addRow, alignItems: 'center', marginBottom: 8 }}>
                <div style={{ minWidth: 180, fontSize: 13 }}>
                  <strong>{emp.name}</strong>{emp.title ? ` — ${emp.title}` : ''}
                </div>
                <input
                  style={styles.addInput}
                  placeholder={`Default: ${emp.email}`}
                  value={engineerOverrideEmails[emp.email] || ''}
                  onChange={(e) => setEngineerOverrideEmails((p) => ({ ...p, [emp.email]: e.target.value }))}
                />
                <button
                  style={styles.addBtn}
                  disabled={sendingEngineerDigest === emp.email}
                  onClick={() => sendEngineerDigestNow(emp)}
                >
                  {sendingEngineerDigest === emp.email ? 'Sending...' : 'Send Now'}
                </button>
                <a
                  href={`${API_BASE}/api/ca-plus/digest/export-engineer?userId=${encodeURIComponent(userId)}&reviewerEmail=${encodeURIComponent(emp.email)}&reviewerName=${encodeURIComponent(emp.name)}`}
                  title="Download as Excel"
                  style={{
                    ...styles.secondaryBtn, textDecoration: 'none', display: 'inline-flex',
                    alignItems: 'center', justifyContent: 'center', padding: '9px 12px', fontSize: 16,
                  }}
                >
                  ⬇
                </a>
              </div>
            ))}
            {!engineerSearch && employees.length > 5 && (
              <button
                style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 13, cursor: 'pointer', textDecoration: 'underline', padding: 0, marginBottom: 12 }}
                onClick={() => setShowAllEngineers((v) => !v)}
              >
                {showAllEngineers ? 'Show less' : `Show all (${employees.length})`}
              </button>
            )}
            {Object.entries(engineerDigestResults).map(([email, result]) => (
              <div key={email} style={{ fontSize: 13, color: result.sent ? '#166534' : '#dc2626', marginTop: 4 }}>
                {email}: {result.sent ? `✓ Sent — ${result.count} open item(s).` : `Failed: ${result.reason || 'unknown error'}`}
              </div>
            ))}
          </div>

          <div style={styles.card}>
            <div style={styles.sectionTitle}>Shop Drawing Stamps</div>
            <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
              Upload a graphic per disposition — each image should already include your logo, the
              pre-checked box, and any legal wording baked in. The app overlays only the date and
              reviewer's name on top. A disposition without one falls back to the generic text-only
              stamp automatically.
            </p>
            {(form.caPlusStampOptions || []).map((opt) => {
              const filename = opt.stampImageRef?.originalname;
              const busy = uploadingStampId === opt.id;
              return (
                <div key={opt.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid #f0ece4' }}>
                  <div style={{ flex: '0 0 260px', fontSize: 13, fontWeight: 600 }}>{opt.label}</div>
                  <div style={{ flex: 1, fontSize: 13, color: filename ? '#166534' : '#9ca3af' }}>
                    {filename ? `✓ ${filename}` : 'No stamp — using generic text-based stamp'}
                  </div>
                  <label style={{ ...styles.addBtn, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 }}>
                    {busy ? 'Uploading...' : filename ? 'Replace' : '+ Upload'}
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      disabled={busy}
                      onChange={(e) => { if (e.target.files[0]) uploadStampFor(opt.id, e.target.files[0]); e.target.value = ''; }}
                      style={{ display: 'none' }}
                    />
                  </label>
                  {filename && (
                    <button
                      style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 12, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                      onClick={() => deleteStampFor(opt.id)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              );
            })}
            <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
              <label style={{ fontSize: 13, color: '#374151' }}>Stamp Color</label>
              <input
                type="color"
                value={form.caPlusStampColor || '#C91717'}
                onChange={e => set('caPlusStampColor', e.target.value)}
                style={{ width: 40, height: 32, padding: 0, border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer' }}
              />
              <span style={{ fontSize: 12, color: '#9ca3af' }}>{form.caPlusStampColor || '#C91717'}</span>
            </div>
          </div>
        </>
      )}

      {/* Save bar */}
      {activeTab !== 4 && (
        <div style={styles.saveBar}>
          <button style={{ ...styles.saveBtn, opacity: saving ? 0.7 : 1 }} onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save Profile'}
          </button>
          {saved && <span style={styles.savedMsg}>✓ Profile saved</span>}
          {error && <span style={styles.errorMsg}>{error}</span>}
        </div>
      )}
    </div>
  );
}
